import { useQuery } from '@apollo/client'
import { useMemo } from 'react'

import { LogoStatus } from '../../../../__generated__/graphql/catalog/graphql'
import { LogoDigitizationSetting } from '../../../../__generated__/graphql/catalog/graphql'
import { useAuthentication } from '../../../../app/_providers/AuthenticationProvider.client'
import { useGraphqlClients } from '../../../../app/_providers/GraphqlClientsProvider.client'
import { CartContainer } from '../../../../brunswick/containers/cart'
import { LogoFile } from '../../../../containers/EmbroideryService/types'
import { MY_EMBROIDERY_LOGOS } from '../../../../lib/graphql/queries'
import { QueryStatus } from '../../../../lib/hooks/types'

/** Get all of the DTC embroidery logo files that are available for a given user */
export default function useEmbroideryLogos() {
  const { authorizedClient: figsAuthedClient } = useGraphqlClients()
  const { cart } = CartContainer.useContainer()
  const { isLoggedIn } = useAuthentication()
  const { data, error, loading } = useQuery(MY_EMBROIDERY_LOGOS, {
    client: figsAuthedClient,
    fetchPolicy: 'network-only',
    skip: !isLoggedIn,
  })

  /*
   * The logo files that are saved to the user's account.
   */
  const userLogos = useMemo<LogoFile[]>(() => {
    const savedLogos = data?.my?.embroideryLogos ?? []
    return savedLogos.map(logo => ({
      digitizationSetting: logo?.digitizationSetting!,
      id: logo?.id ?? '',
      name: logo?.name ?? '',
      notes: logo?.notes ?? '',
      preview: logo?.preview ?? '',
      shopifyCustomerId: '',
      status: logo?.status as LogoStatus,
    }))
  }, [data])

  /*
   * A "transient" logo is a logo + digitization request that exists in the user's cart, but has not yet been purchased/saved.
   * Only one of these should exist at any given time at most.
   */
  const transientLogo = useMemo<LogoFile | undefined>(() => {
    const cartItem = Object.values(cart.items).find(item => {
      return item.productType.toUpperCase() === 'EMBROIDERY' && item.properties._logo_setup_guid
    })

    if (!cartItem) return undefined

    return {
      digitizationSetting: cartItem.properties.logo_color_option as LogoDigitizationSetting,
      id: cartItem?.properties._logo_id!,
      name: cartItem?.properties.logo_name!,
      notes: cartItem?.properties.logo_note!,
      preview: cartItem?.properties._logo_preview!,
      shopifyCustomerId: cartItem?.properties.shopify_customer_id!,
      status: cartItem?.properties.status as LogoStatus,
    }
  }, [cart.items])

  const status = useMemo<QueryStatus>(() => {
    if (loading) return 'pending'
    if (error) return 'rejected'
    if (data) return 'resolved'
    return 'idle'
  }, [loading, error, data])

  return { userLogos, transientLogo, status }
}
